(() => {
  const listing = document.querySelector('[data-comparison-listing]');
  if (!listing) return;

  const grid = listing.querySelector('[data-comparison-grid]');
  const pagination = listing.querySelector('[data-comparison-pagination]');
  if (!grid || !pagination) return;

  const sectionId = listing.dataset.sectionId;
  const pageParam = listing.dataset.pageParam;
  const totalItems = Number(listing.dataset.totalItems || 0);
  const batchSize = 6;
  const mobileSize = 3;
  const cache = new Map();
  const initialBatch = Number(listing.dataset.serverPage || 1);
  cache.set(initialBatch, Array.from(grid.children));

  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
  const pageCount = () => Math.ceil(totalItems / (isMobile() ? mobileSize : batchSize));
  const batchForPage = (page) => isMobile() ? Math.ceil(page / 2) : page;
  const offsetForPage = (page) => isMobile() ? ((page - 1) % 2) * mobileSize : 0;
  const currentServerPage = () => Number(new URL(window.location.href).searchParams.get(pageParam) || initialBatch);
  let currentPage = Number(history.state?.comparisonPage || new URL(window.location.href).searchParams.get('comparison_page') || (isMobile() ? (initialBatch - 1) * 2 + 1 : initialBatch));
  let renderedPageCount = null;
  let renderedMobileMode = null;
  let loading = false;

  async function loadBatch(batch) {
    if (cache.has(batch)) return cache.get(batch);
    const url = new URL(window.location.href);
    url.searchParams.set(pageParam, String(batch));
    url.searchParams.set('sections', sectionId);
    url.searchParams.delete('section_id');
    const response = await fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error(`Could not load comparisons (${response.status})`);
    const sections = await response.json();
    const html = sections[sectionId];
    if (!html) throw new Error('The comparison section was not returned');
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fetchedGrid = parsed.querySelector('[data-comparison-grid]');
    if (!fetchedGrid) throw new Error('Comparison cards were missing from the response');
    const cards = Array.from(fetchedGrid.children);
    cache.set(batch, cards);
    return cards;
  }

  function setCards(cards, offset) {
    const visibleCards = cards.slice(offset, offset + (isMobile() ? mobileSize : batchSize));
    grid.replaceChildren(...visibleCards.map((card) => card.cloneNode(true)));
  }

  function renderPagination(forceRebuild = false) {
    const pages = pageCount();
    if (pages <= 1) {
      pagination.hidden = true;
      pagination.replaceChildren();
      renderedPageCount = pages;
      renderedMobileMode = isMobile();
      return;
    }
    pagination.hidden = false;
    const mobileMode = isMobile();
    const rebuild = forceRebuild || renderedPageCount !== pages || renderedMobileMode !== mobileMode || !pagination.querySelector('.competitor-pagination__pages button');
    if (rebuild) {
      pagination.replaceChildren();
      const back = document.createElement('button');
      back.type = 'button';
      back.className = 'competitor-pagination__back';
      back.innerHTML = '<span aria-hidden="true">‹</span> Back';
      back.addEventListener('click', () => goTo(currentPage - 1));
      pagination.append(back);

      const pageButtons = document.createElement('div');
      pageButtons.className = 'competitor-pagination__pages';
      for (let page = 1; page <= pages; page += 1) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = String(page);
        button.setAttribute('aria-label', `Go to page ${page}`);
        button.addEventListener('click', () => goTo(page));
        pageButtons.append(button);
      }
      pagination.append(pageButtons);

      const next = document.createElement('button');
      next.type = 'button';
      next.className = 'competitor-pagination__next';
      next.innerHTML = 'Next <span aria-hidden="true">›</span>';
      next.addEventListener('click', () => goTo(currentPage + 1));
      pagination.append(next);
      renderedPageCount = pages;
      renderedMobileMode = mobileMode;
    }

    const back = pagination.querySelector('.competitor-pagination__back');
    back.disabled = loading || currentPage <= 1;
    pagination.querySelectorAll('.competitor-pagination__pages button').forEach((button, index) => {
      const active = index + 1 === currentPage;
      button.disabled = loading;
      button.classList.toggle('is-current', active);
      if (active) button.setAttribute('aria-current', 'page');
      else button.removeAttribute('aria-current');
    });
    const next = pagination.querySelector('.competitor-pagination__next');
    next.disabled = loading || currentPage >= pages;
  }

  async function showPage(page, preservePaginationPosition = false) {
    const batch = batchForPage(page);
    const cards = await loadBatch(batch);
    const paginationTop = preservePaginationPosition ? pagination.getBoundingClientRect().top : null;
    currentPage = page;
    setCards(cards, offsetForPage(page));
    renderPagination();
    if (preservePaginationPosition) {
      window.scrollBy({ top: pagination.getBoundingClientRect().top - paginationTop, behavior: 'instant' });
    }
  }

  async function goTo(page, addHistory = true) {
    if (loading || page < 1 || page > pageCount()) return;
    loading = true;
    pagination.setAttribute('aria-busy', 'true');
    pagination.querySelectorAll('button').forEach((button) => { button.disabled = true; });
    try {
      const batch = batchForPage(page);
      await showPage(page, addHistory);
      if (addHistory) {
        const url = new URL(window.location.href);
        url.searchParams.set(pageParam, String(batch));
        url.searchParams.set('comparison_page', String(page));
        history.pushState({ comparisonPage: page }, '', url);
      }
    } catch (error) {
      console.error('[competitor comparison pagination]', error);
    } finally {
      loading = false;
      pagination.removeAttribute('aria-busy');
      renderPagination();
    }
  }

  pagination.addEventListener('click', (event) => {
    const link = event.target.closest('a');
    if (!link) return;
    event.preventDefault();
  });

  window.addEventListener('popstate', () => {
    const page = Number(history.state?.comparisonPage || new URL(window.location.href).searchParams.get('comparison_page') || (isMobile() ? (currentServerPage() - 1) * 2 + 1 : currentServerPage()));
    goTo(Math.min(Math.max(page, 1), pageCount()), false);
  });

  let wasMobile = isMobile();
  window.addEventListener('resize', () => {
    if (isMobile() === wasMobile) return;
    const oldSize = wasMobile ? mobileSize : batchSize;
    wasMobile = isMobile();
    const newSize = wasMobile ? mobileSize : batchSize;
    const page = Math.min(Math.floor(((currentPage - 1) * oldSize) / newSize) + 1, pageCount());
    goTo(page, false);
  }, { passive: true });

  const firstPage = Math.min(Math.max(currentPage, 1), pageCount());
  if (batchForPage(firstPage) === initialBatch) {
    currentPage = firstPage;
    if (offsetForPage(firstPage) > 0) setCards(cache.get(initialBatch), offsetForPage(firstPage));
    renderPagination(true);
  } else {
    goTo(firstPage, false);
  }
})();
